import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './ScannerScanView.css';

// iOS ActivityType enum raw values (Models/ActivityType.swift) — iOS decodes
// activityType strictly, so these must match exactly.
const ACTIVITY_TYPES = [
  'School Entrance',
  'School Exit',
  'School Bus Boarding',
  'School Bus Drop-off',
  'Classroom Entry',
  'Library Visit',
  'Cafeteria Visit',
  'After-School Programme',
  'School Event',
  'Office Visit',
  'General Student Scan',
];

// iOS decodes dates with ISO8601DateFormatter (.withInternetDateTime) which does
// NOT accept fractional seconds — strip ".SSS" so the string parses on iOS.
const toIsoString = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');
const toDayString = (d) => d.toISOString().slice(0, 10); // yyyy-MM-dd

function ScannerScanView({ scannerData }) {
  const [studentIdInput, setStudentIdInput] = useState('');
  const [student, setStudent] = useState(null);
  const [studentNotFound, setStudentNotFound] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedActivityType, setSelectedActivityType] = useState(ACTIVITY_TYPES[0]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [lookingUpStudent, setLookingUpStudent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanLoopRef = useRef(null);

  const loadLocations = useCallback(async () => {
    if (!scannerData?.schoolId) {
      setLocationsLoading(false);
      return;
    }
    try {
      setLocationsLoading(true);
      const q = query(
        collection(db, 'scanLocations'),
        where('schoolId', '==', scannerData.schoolId),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const locs = [];
      snapshot.forEach((d) => {
        locs.push({ id: d.id, ...d.data() });
      });
      setLocations(locs);
      if (locs.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locs[0].id);
      }
    } catch (error) {
      console.error('Error loading scan locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  }, [scannerData?.schoolId, selectedLocationId]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const lookupStudent = useCallback(async (idToLookup) => {
    const trimmed = (idToLookup || '').trim();
    if (!trimmed) {
      setStudent(null);
      setStudentNotFound(false);
      return;
    }
    try {
      setLookingUpStudent(true);
      setStudentNotFound(false);
      setStudent(null);

      // 1. Search top-level students collection
      const topQ = query(collection(db, 'students'), where('studentId', '==', trimmed));
      const topSnapshot = await getDocs(topQ);
      if (!topSnapshot.empty) {
        const docSnap = topSnapshot.docs[0];
        setStudent({ id: docSnap.id, ...docSnap.data() });
        setLookingUpStudent(false);
        return;
      }

      // 2. Search class subcollections (iOS structure)
      const classesQ = scannerData?.schoolId
        ? query(collection(db, 'classes'), where('schoolId', '==', scannerData.schoolId))
        : query(collection(db, 'classes'));
      const classesSnapshot = await getDocs(classesQ);
      for (const classDoc of classesSnapshot.docs) {
        const classId = classDoc.id;
        const classData = classDoc.data();
        const subQ = query(
          collection(db, 'classes', classId, 'students'),
          where('studentId', '==', trimmed)
        );
        const subSnapshot = await getDocs(subQ);
        if (!subSnapshot.empty) {
          const docSnap = subSnapshot.docs[0];
          setStudent({ id: docSnap.id, classId, className: classData.name, ...docSnap.data() });
          setLookingUpStudent(false);
          return;
        }
      }

      setStudentNotFound(true);
    } catch (error) {
      console.error('Error looking up student:', error);
      setStudentNotFound(true);
    } finally {
      setLookingUpStudent(false);
    }
  }, [scannerData?.schoolId]);

  const handleInputChange = (e) => {
    setStudentIdInput(e.target.value);
    setStudent(null);
    setStudentNotFound(false);
  };

  const handleLookup = () => {
    lookupStudent(studentIdInput);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      lookupStudent(studentIdInput);
    }
  };

  const handleDetected = useCallback(
    (code) => {
      if (!code) return;
      setStudentIdInput(code);
      setCameraActive(false);
      lookupStudent(code);
    },
    [lookupStudent]
  );

  const stopCamera = useCallback(() => {
    if (scanLoopRef.current) {
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    detectorRef.current = null;
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);

      // Use native BarcodeDetector if available (Chrome on Android, some desktop)
      if ('BarcodeDetector' in window) {
        try {
          detectorRef.current = new window.BarcodeDetector({
            formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a'],
          });
        } catch (e) {
          detectorRef.current = null;
        }
      }

      const detectLoop = async () => {
        if (!videoRef.current || !streamRef.current) return;
        if (detectorRef.current) {
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes && codes.length > 0) {
              const value = codes[0].rawValue;
              if (value) {
                handleDetected(value);
                return;
              }
            }
          } catch (e) {
            // ignore detection errors
          }
        }
        scanLoopRef.current = requestAnimationFrame(detectLoop);
      };
      scanLoopRef.current = requestAnimationFrame(detectLoop);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError(
        'Unable to access camera. You can still enter the student ID manually below.'
      );
      setCameraActive(false);
    }
  }, [handleDetected]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const toggleCamera = () => {
    if (cameraActive) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  const handleLogActivity = async () => {
    setMessage(null);
    if (!student) {
      setMessage({ type: 'error', text: 'Please look up a valid student first.' });
      return;
    }
    if (!selectedLocationId) {
      setMessage({ type: 'error', text: 'Please select a scan location.' });
      return;
    }
    if (!scannerData?.uid) {
      setMessage({ type: 'error', text: 'Scanner information is missing.' });
      return;
    }

    const selectedLocation = locations.find((l) => l.id === selectedLocationId);
    const locationName = selectedLocation?.name || selectedLocation?.locationName || 'Unknown';

    try {
      setSubmitting(true);
      // iOS StudentActivityLog requires: id (UUID), studentId, studentName,
      // studentDocumentId, scannerId, scannerName, scannerRole, activityType,
      // timestamp + createdAt (ISO strings — NOT serverTimestamp), date
      // (yyyy-MM-dd), and `location` (web readers use `locationName`, so write both).
      const now = new Date();
      const logId = crypto.randomUUID();
      await setDoc(doc(db, 'studentActivityLogs', logId), {
        id: logId,
        studentId: student.studentId || student.id,
        studentName: student.fullName || student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student',
        studentDocumentId: student.id,
        scannerId: scannerData.uid,
        scannerName: scannerData.displayName || '',
        scannerRole: scannerData.role || 'Scanner',
        activityType: selectedActivityType,
        timestamp: toIsoString(now),
        date: toDayString(now),
        location: locationName,
        locationId: selectedLocationId,
        locationName: locationName,
        classId: student.classId || null,
        device: 'Web Portal',
        createdAt: toIsoString(now),
        schoolId: scannerData.schoolId || null,
      });
      setMessage({
        type: 'success',
        text: `Activity logged for ${student.fullName || student.name || 'student'}.`,
      });
      setStudentIdInput('');
      setStudent(null);
      setStudentNotFound(false);
    } catch (error) {
      console.error('Error logging activity:', error);
      setMessage({ type: 'error', text: 'Failed to log activity: ' + error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="scanner-scan-view">
      <h2>Scan Student</h2>

      <div className="scan-card">
        <div className="scan-input-section">
          <label className="scan-label">Student ID / Code</label>
          <div className="scan-input-row">
            <input
              type="text"
              className="scan-input"
              placeholder="Enter student ID or scan QR code"
              value={studentIdInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={lookingUpStudent}
            />
            <button
              className="scan-lookup-button"
              onClick={handleLookup}
              disabled={lookingUpStudent || !studentIdInput.trim()}
            >
              {lookingUpStudent ? '...' : 'Look Up'}
            </button>
            <button
              className={`camera-button ${cameraActive ? 'active' : ''}`}
              onClick={toggleCamera}
              title={cameraActive ? 'Stop camera' : 'Scan with camera'}
            >
              {cameraActive ? '⏹' : '📷'}
            </button>
          </div>
          {cameraError && <p className="camera-error">{cameraError}</p>}
        </div>

        {cameraActive && (
          <div className="camera-preview">
            <video ref={videoRef} className="camera-video" muted playsInline />
            <div className="camera-frame" />
            <p className="camera-hint">Point camera at a QR code</p>
          </div>
        )}

        {studentNotFound && (
          <div className="student-not-found">
            <span className="not-found-icon">❌</span>
            <p>No student found with that ID.</p>
          </div>
        )}

        {student && (
          <div className="student-confirmation-card">
            <div className="student-confirmation-header">
              <span className="student-avatar">👤</span>
              <div className="student-confirmation-info">
                <h3>{student.fullName || student.name || 'Student'}</h3>
                <p>Student ID: {student.studentId || student.id}</p>
                {student.classId && <p>Class: {student.classId}</p>}
                {student.className && <p>Class: {student.className}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="scan-form-row">
          <div className="scan-field">
            <label className="scan-label">Scan Location</label>
            {locationsLoading ? (
              <p className="field-loading">Loading locations...</p>
            ) : locations.length === 0 ? (
              <p className="field-empty">No active scan locations for your school.</p>
            ) : (
              <select
                className="scan-select"
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
              >
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name || loc.locationName || 'Unnamed location'}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="scan-field">
            <label className="scan-label">Activity Type</label>
            <select
              className="scan-select"
              value={selectedActivityType}
              onChange={(e) => setSelectedActivityType(e.target.value)}
            >
              {ACTIVITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {message && (
          <div className={`scan-message ${message.type}`}>
            {message.type === 'success' ? '✅ ' : '⚠️ '}
            {message.text}
          </div>
        )}

        <button
          className="log-activity-button"
          onClick={handleLogActivity}
          disabled={submitting || !student || !selectedLocationId}
        >
          {submitting ? 'Logging...' : 'Log Activity'}
        </button>
      </div>
    </div>
  );
}

export default ScannerScanView;
